import {
  bootstrapAdmissionPendingBilling,
  getBillingDocumentByAdmission,
} from "@/core/billing/billing-documents/billing-documents-store";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";
import { shouldAutoSeedDemoData } from "@/core/demo/is-demo-mode-enabled";
import {
  apiCreateAdmission,
  apiListAdmissions,
  apiSyncAdmission,
} from "@/core/hospitol-api-client";
import {
  markPharmacySotMigratedIfRemoteConfirmed,
  probeHospitolDurableConfigured,
  shouldSeedPharmacyDemoData,
} from "@/core/pharmacy/pharmacy-durable-sot";
import { getPatientById, listPatients } from "@/core/patients/patient-store";
import { patientFullName } from "@/core/patients/types";
import {
  enqueueAdmissionAtReception,
  enqueueFromOrientation,
  enqueuePaidConsultationAtReception,
} from "@/core/queue/service-queue-store";
import { createStayFromAdmission } from "@/core/hospitalization/hospitalization-store";
import {
  canOrientToClinicalConsultation,
  requiresConsultationPaymentGate,
} from "./coverage-rules";
import type {
  Admission,
  AdmissionChannel,
  AdmissionInput,
  AdmissionStatus,
  EmergencyTriagePatch,
  TriageLevel,
} from "./types";

const ADMISSIONS_KEY = "hospitol.admissions";
const DEMO_SEED_KEY = "hospitol.admissions.demoSeeded";

function replaceAdmissionsCache(admissions: Admission[]): void {
  writeJson(ADMISSIONS_KEY, admissions);
}

function replicateAdmissionToApi(admission: Admission): void {
  void apiSyncAdmission(admission);
}

function mergeApiAdmission(localId: string, apiAdmission: Admission): void {
  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === localId);
  if (index >= 0) {
    list[index] = apiAdmission;
    writeJson(ADMISSIONS_KEY, list);
  }
}

function normalizeAdmission(raw: Record<string, unknown>): Admission {
  const channel = (raw.channel as AdmissionChannel) ?? "admission";
  const glasgowRaw = raw.glasgowScore;
  const glasgowScore =
    glasgowRaw === null || glasgowRaw === undefined || glasgowRaw === ""
      ? null
      : Number(glasgowRaw);
  return {
    id: String(raw.id ?? ""),
    patientId: String(raw.patientId ?? ""),
    channel,
    status: (raw.status as AdmissionStatus) ?? "waiting",
    triageLevel: (raw.triageLevel as TriageLevel | null) ?? null,
    motif: String(raw.motif ?? ""),
    destination: String(raw.destination ?? ""),
    destinations: Array.isArray(raw.destinations) ? (raw.destinations as string[]) : [],
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    orientedAt: raw.orientedAt ? String(raw.orientedAt) : null,
    walkInName: String(raw.walkInName ?? ""),
    billingDocumentId: raw.billingDocumentId ? String(raw.billingDocumentId) : null,
    blockedDestinations: Array.isArray(raw.blockedDestinations)
      ? (raw.blockedDestinations as string[])
      : [],
    arrivalMode: channel === "emergency" ? ((raw.arrivalMode as Admission["arrivalMode"]) ?? "walk_in") : null,
    glasgowScore:
      channel === "emergency" && Number.isFinite(glasgowScore) ? Math.min(15, Math.max(3, glasgowScore)) : null,
    allergiesNote: channel === "emergency" ? String(raw.allergiesNote ?? "") : "",
    triageNotes: channel === "emergency" ? String(raw.triageNotes ?? "") : "",
    triagedAt: raw.triagedAt ? String(raw.triagedAt) : null,
  };
}

function readAdmissions(): Admission[] {
  return readJson<Record<string, unknown>[]>(ADMISSIONS_KEY, []).map(normalizeAdmission);
}

function isToday(iso: string): boolean {
  const date = new Date(iso);
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
}

function sortByCreatedDesc(a: Admission, b: Admission): number {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

const TRIAGE_PRIORITY: Record<TriageLevel, number> = {
  red: 0,
  yellow: 1,
  green: 2,
};

function sortEmergencyAdmissions(a: Admission, b: Admission): number {
  const activeA = a.status !== "oriented" ? 0 : 1;
  const activeB = b.status !== "oriented" ? 0 : 1;
  if (activeA !== activeB) return activeA - activeB;

  const pa = a.triageLevel ? TRIAGE_PRIORITY[a.triageLevel] : 3;
  const pb = b.triageLevel ? TRIAGE_PRIORITY[b.triageLevel] : 3;
  if (pa !== pb) return pa - pb;

  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

export function admissionCustomerName(admission: Admission): string {
  const patient = getPatientById(admission.patientId);
  if (patient) return patientFullName(patient);
  return admission.walkInName.trim() || "Visiteur";
}

export function ensureDemoAdmissions() {
  if (!shouldAutoSeedDemoData()) return;
  if (!shouldSeedPharmacyDemoData()) return;
  if (getStorageItem(DEMO_SEED_KEY)) return;

  const patients = listPatients();
  if (patients.length === 0) return;

  const [p1, p2, p3] = patients;
  const now = Date.now();

  if (p1) {
    const adm = createAdmission({
      patientId: p1.id,
      channel: "admission",
      motif: "Contrôle de routine",
    });
    updateAdmissionStatus(adm.id, "in_progress");
  }

  if (p2) {
    createAdmission({
      patientId: p2.id,
      channel: "admission",
      motif: "Douleurs abdominales",
    });
  }

  if (p3) {
    const emergency = createAdmission({
      patientId: p3.id,
      channel: "emergency",
      motif: "Traumatisme membre supérieur",
      triageLevel: "yellow",
    });
    updateAdmissionTriage(emergency.id, "yellow");
    updateAdmissionStatus(emergency.id, "in_progress");
  }

  const list = readAdmissions();
  if (list[0]) {
    list[0] = {
      ...list[0],
      createdAt: new Date(now - 86_400_000).toISOString(),
    };
    writeJson(ADMISSIONS_KEY, list);
  }

  setStorageItem(DEMO_SEED_KEY, new Date().toISOString());
}

export function listAdmissions(channel?: AdmissionChannel): Admission[] {
  ensureDemoAdmissions();
  const list = readAdmissions();
  const filtered = channel ? list.filter((a) => a.channel === channel) : list;
  if (channel === "emergency") {
    return filtered.sort(sortEmergencyAdmissions);
  }
  return filtered.sort(sortByCreatedDesc);
}

/** Télécharge les admissions depuis l'API (SoT si durable) et met à jour le cache local. */
export async function syncAdmissionsFromApi(): Promise<boolean> {
  const remote = await apiListAdmissions();
  if (remote === null) return false;

  const durable = await probeHospitolDurableConfigured();

  if (remote.length > 0) {
    replaceAdmissionsCache(remote);
    markPharmacySotMigratedIfRemoteConfirmed("admissions", durable, remote.length);
    return true;
  }

  const local = readAdmissions();
  if (local.length > 0) {
    // WP-C1: remote empty + local populated → push LS→API (never silent discard).
    for (const admission of local) {
      await apiSyncAdmission(admission);
    }
    const again = await apiListAdmissions();
    if (again === null) return false;
    if (again.length > 0) {
      replaceAdmissionsCache(again);
      markPharmacySotMigratedIfRemoteConfirmed("admissions", durable, again.length);
    }
    return true;
  }

  return true;
}

export function listAdmissionsByStatus(
  status: AdmissionStatus,
  channel?: AdmissionChannel,
): Admission[] {
  return listAdmissions(channel).filter((a) => a.status === status);
}

export function getAdmissionById(id: string): Admission | null {
  return listAdmissions().find((a) => a.id === id) ?? null;
}

export function createAdmission(input: AdmissionInput): Admission {
  const now = new Date().toISOString();
  const admission: Admission = {
    id: `adm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    patientId: input.patientId,
    channel: input.channel,
    status: "waiting",
    triageLevel: input.channel === "emergency" ? (input.triageLevel ?? "green") : null,
    motif: input.motif.trim(),
    destination: "",
    destinations: [],
    walkInName: input.walkInName?.trim() ?? "",
    billingDocumentId: null,
    blockedDestinations: [],
    createdAt: now,
    updatedAt: now,
    orientedAt: null,
    arrivalMode: input.channel === "emergency" ? (input.arrivalMode ?? "walk_in") : null,
    glasgowScore:
      input.channel === "emergency" && input.glasgowScore != null
        ? Math.min(15, Math.max(3, input.glasgowScore))
        : null,
    allergiesNote: input.channel === "emergency" ? (input.allergiesNote?.trim() ?? "") : "",
    triageNotes: input.channel === "emergency" ? (input.triageNotes?.trim() ?? "") : "",
    triagedAt: input.channel === "emergency" ? now : null,
  };
  const list = readAdmissions();
  list.unshift(admission);
  writeJson(ADMISSIONS_KEY, list);

  if (input.channel === "admission") {
    enqueueAdmissionAtReception({
      admissionId: admission.id,
      patientId: admission.patientId,
      motif: admission.motif,
    });
  }

  void apiCreateAdmission(input).then((apiAdmission) => {
    if (!apiAdmission) {
      replicateAdmissionToApi(admission);
      return;
    }
    mergeApiAdmission(admission.id, apiAdmission);
  });

  return admission;
}

export function linkAdmissionBillingDocument(
  admissionId: string,
  billingDocumentId: string,
): Admission | null {
  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === admissionId);
  if (index < 0) return null;
  list[index] = {
    ...list[index]!,
    billingDocumentId,
    updatedAt: new Date().toISOString(),
  };
  writeJson(ADMISSIONS_KEY, list);
  replicateAdmissionToApi(list[index]!);
  return list[index]!;
}

export function updateAdmissionStatus(id: string, status: AdmissionStatus): Admission | null {
  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const current = list[index]!;
  const updated: Admission = {
    ...current,
    status,
    updatedAt: now,
    orientedAt: status === "oriented" ? now : current.orientedAt,
  };
  list[index] = updated;
  writeJson(ADMISSIONS_KEY, list);
  replicateAdmissionToApi(updated);
  return updated;
}

export type OrientationResult = {
  admission: Admission;
  enqueuedDestinations: string[];
  blockedDestinations: string[];
  sentToBilling: boolean;
};

export function orientAdmission(id: string, destinations: string[]): OrientationResult | null {
  const admission = getAdmissionById(id);
  if (!admission) return null;

  const cleaned = destinations.map((d) => d.trim()).filter(Boolean);
  const patient = getPatientById(admission.patientId);

  const allowed: string[] = [];
  const blocked: string[] = [];

  for (const dest of cleaned) {
    if (/comptabilit/i.test(dest)) continue;
    if (admission.channel === "emergency" || !requiresConsultationPaymentGate(dest)) {
      allowed.push(dest);
      continue;
    }
    if (!patient) {
      blocked.push(dest);
      continue;
    }
    const gate = canOrientToClinicalConsultation(patient, admission.id);
    if (gate.allowed) {
      allowed.push(dest);
    } else {
      blocked.push(dest);
    }
  }

  if (allowed.length === 0) {
    if (blocked.length > 0) {
      const now = new Date().toISOString();
      const billingHold: Admission = {
        ...admission,
        status: "pending_billing",
        blockedDestinations: blocked,
        updatedAt: now,
      };
      const list = readAdmissions();
      const index = list.findIndex((a) => a.id === id);
      if (index >= 0) {
        list[index] = billingHold;
        writeJson(ADMISSIONS_KEY, list);
        replicateAdmissionToApi(billingHold);
      }
      const patientName = patient
        ? patientFullName(patient)
        : admission.walkInName.trim() || "Patient";
      const serviceLabel =
        blocked.find((d) => /médecine|consultation|général/i.test(d)) ?? blocked[0] ?? "Médecine générale";
      const billingDoc = bootstrapAdmissionPendingBilling({
        admissionId: id,
        patientId: admission.patientId,
        serviceLabel,
        customerName: patientName,
      });
      linkAdmissionBillingDocument(id, billingDoc.id);
      return {
        admission: { ...billingHold, billingDocumentId: billingDoc.id },
        enqueuedDestinations: [],
        blockedDestinations: blocked,
        sentToBilling: true,
      };
    }
    return {
      admission,
      enqueuedDestinations: [],
      blockedDestinations: blocked,
      sentToBilling: false,
    };
  }

  const now = new Date().toISOString();
  const updated: Admission = {
    ...admission,
    status: "oriented",
    destination: allowed.join(", "),
    destinations: allowed,
    blockedDestinations: blocked,
    updatedAt: now,
    orientedAt: now,
  };

  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === id);
  if (index >= 0) {
    list[index] = updated;
    writeJson(ADMISSIONS_KEY, list);
    replicateAdmissionToApi(updated);
  }

  enqueueFromOrientation(updated, allowed);

  void import("@/core/billing/extended-hospital-billing").then(({ autoBillAdmissionOnOrient }) =>
    autoBillAdmissionOnOrient(updated),
  );

  for (const dest of allowed) {
    if (/hospital/i.test(dest)) {
      createStayFromAdmission({
        patientId: updated.patientId,
        admissionId: updated.id,
        motif: updated.motif,
        wardName: dest,
      });
    }
  }

  return {
    admission: updated,
    enqueuedDestinations: allowed,
    blockedDestinations: blocked,
    sentToBilling: false,
  };
}

export function handleConsultationPaid(admissionId: string, billingDocumentId: string): void {
  const admission = getAdmissionById(admissionId);
  if (!admission) return;

  const doc = getBillingDocumentByAdmission(admissionId);
  if (!doc || doc.id !== billingDocumentId) return;

  enqueuePaidConsultationAtReception({
    admissionId,
    patientId: admission.patientId,
    serviceLabel: doc.serviceLabel || "Médecine générale",
    motif: admission.motif,
    billingDocumentId: doc.id,
  });

  if (admission.status === "pending_billing") {
    updateAdmissionStatus(admissionId, "in_progress");
  }
}

export function updateAdmissionTriage(id: string, level: TriageLevel): Admission | null {
  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === id);
  if (index < 0) return null;

  const now = new Date().toISOString();
  const updated: Admission = {
    ...list[index]!,
    triageLevel: level,
    triagedAt: list[index]!.channel === "emergency" ? now : list[index]!.triagedAt,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(ADMISSIONS_KEY, list);
  replicateAdmissionToApi(updated);
  return updated;
}

export function updateEmergencyTriage(id: string, patch: EmergencyTriagePatch): Admission | null {
  const list = readAdmissions();
  const index = list.findIndex((a) => a.id === id);
  if (index < 0 || list[index]!.channel !== "emergency") return null;

  const now = new Date().toISOString();
  const current = list[index]!;
  const glasgow =
    patch.glasgowScore === undefined
      ? current.glasgowScore
      : patch.glasgowScore === null
        ? null
        : Math.min(15, Math.max(3, patch.glasgowScore));

  const updated: Admission = {
    ...current,
    triageLevel: patch.triageLevel ?? current.triageLevel,
    arrivalMode: patch.arrivalMode ?? current.arrivalMode,
    glasgowScore: glasgow,
    allergiesNote: patch.allergiesNote !== undefined ? patch.allergiesNote.trim() : current.allergiesNote,
    triageNotes: patch.triageNotes !== undefined ? patch.triageNotes.trim() : current.triageNotes,
    triagedAt: now,
    updatedAt: now,
  };
  list[index] = updated;
  writeJson(ADMISSIONS_KEY, list);
  replicateAdmissionToApi(updated);
  return updated;
}

export function countTodayPatients(): number {
  const today = listAdmissions().filter((a) => isToday(a.createdAt));
  return new Set(today.map((a) => a.patientId)).size;
}

export function countTodayAdmissions(channel?: AdmissionChannel): number {
  return listAdmissions(channel).filter((a) => isToday(a.createdAt)).length;
}

export function getTodayAdmissionStats(): {
  totalPatients: number;
  admissions: number;
  emergency: number;
  waiting: number;
  inProgress: number;
  oriented: number;
  pendingBilling: number;
} {
  const today = listAdmissions().filter((a) => isToday(a.createdAt));
  return {
    totalPatients: new Set(today.map((a) => a.patientId)).size,
    admissions: today.filter((a) => a.channel === "admission").length,
    emergency: today.filter((a) => a.channel === "emergency").length,
    waiting: today.filter((a) => a.status === "waiting").length,
    inProgress: today.filter((a) => a.status === "in_progress").length,
    oriented: today.filter((a) => a.status === "oriented").length,
    pendingBilling: today.filter((a) => a.status === "pending_billing").length,
  };
}

/** Écoute paiement caisse → file accueil consultations payées. */
export function subscribeConsultationPaid(handler: (admissionId: string, docId: string) => void): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ document: { admissionId: string | null; id: string } }>).detail;
    if (detail?.document?.admissionId) {
      handler(detail.document.admissionId, detail.document.id);
    }
  };
  window.addEventListener("hospitol:consultation-paid", listener);
  return () => window.removeEventListener("hospitol:consultation-paid", listener);
}
